import { useId, type KeyboardEvent } from 'react';
import './SegmentedControl.css';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
  /**
   * Why this segment is dead, rendered as a visible sentence beneath the group — DESIGN.md §6.2
   * "never a disabled control with no stated reason". No shipped screen disables a single segment
   * today (the one that did, `VocalChannelControl`'s "Off", is now a button of its own); this
   * exists so that the first one to need it cannot ship a silently dead segment.
   *
   * MVP-375: it used to be a `title` attribute on the disabled `<button role="radio">`. A tooltip
   * on a disabled control states the reason to nobody — the element takes no focus and no hover on
   * a touch screen, so the only reader who ever saw it was a mouse user who happened to rest the
   * pointer on a control they had already been told they could not use. Same shape MVP-328 and
   * MVP-364 removed everywhere else.
   */
  disabledReason?: string;
}

export interface SegmentedControlProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  /** Visible group label — rendered as the `role="radiogroup"`'s accessible name, not as text
   * (pass a `<span>` above this component for a visible caption; DESIGN.md's Round-3 player pass
   * uses this for "a frequently-used exclusive choice ... one click, never a Select"). */
  label: string;
}

/** DESIGN.md §9.1 `SegmentedControl` (UI-RESEARCH.md Round 3, takeaway 4: Circle's "Noise
 * suppression profile" row). A `role="radiogroup"` of buttons, one always pressed — native
 * `<input type="radio">` semantics without the default OS radio-dot rendering, which this design
 * language does not use anywhere. Arrow-key roving matches the native radiogroup convention. */
export function SegmentedControl<T extends string = string>({ value, onChange, options, label }: SegmentedControlProps<T>) {
  const groupId = useId();

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const enabled = options.map((o, i) => ({ o, i })).filter(({ o }) => !o.disabled);
    if (enabled.length === 0) return;
    const pos = enabled.findIndex(({ i }) => i === index);
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    onChange(next.o.value);
  }

  // MVP-375: only the segments that are BOTH disabled and carry a reason get a sentence. A
  // disabled segment with no reason given is still a defect, but inventing filler copy for it here
  // would hide that from whoever ships one rather than fix it.
  const stated = options.filter((opt) => opt.disabled && opt.disabledReason);

  return (
    <div className="segmented-control__group">
      <div className="segmented-control" role="radiogroup" aria-label={label}>
        {options.map((opt, i) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={opt.value === value}
            disabled={opt.disabled}
            aria-describedby={opt.disabled && opt.disabledReason ? `${groupId}-${opt.value}` : undefined}
            tabIndex={opt.value === value ? 0 : -1}
            className={['segmented-control__option', opt.value === value ? 'segmented-control__option--active' : ''].filter(Boolean).join(' ')}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {stated.map((opt) => (
        <span key={opt.value} id={`${groupId}-${opt.value}`} className="segmented-control__reason text-meta">
          {opt.disabledReason}
        </span>
      ))}
    </div>
  );
}
